import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import FakeLoading from '../ui/FakeLoading';
import { API_BASE_URL } from '../../config';

const GIAIAnalysisModal = ({
  isOpen,
  onClose,
  questionData,
  clientId,
  fetchWithAuth,
  startDate,
  endDate
}) => {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [analysisMetadata, setAnalysisMetadata] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const analysisContentRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Calculer le nombre total de réponses depuis les détails des surveys
  const totalResponsesCount = useMemo(() => {
    if (!questionData?.surveyDetails) return 0;
    return questionData.surveyDetails.reduce((sum, survey) => sum + (survey.answers_count || 0), 0);
  }, [questionData]);

  // Réinitialiser l'état quand la modale s'ouvre/ferme
  useEffect(() => {
    if (!isOpen) {
      setAnalysis(null);
      setAnalysisError(null);
      setAnalysisMetadata(null);
      setIsAnalyzing(false);
      if (abortControllerRef.current) abortControllerRef.current.abort();
    }
  }, [isOpen]);

  // Fonction pour lancer l'analyse IA
  const handleAnalyze = async () => {
    if (!questionData?.surveyDetails || questionData.surveyDetails.length === 0) {
      setAnalysisError("Aucune donnée de survey disponible pour l'analyse.");
      return;
    }

    if (!startDate || !endDate) {
      setAnalysisError("Les dates de début et fin sont requises pour l'analyse.");
      return;
    }

    setIsAnalyzing(true);
    setAnalysisError(null);
    setAnalysis(''); // allow progressive rendering while streaming
    setAnalysisMetadata(null);

    try {
      // Préparer les couples surveyId/questionId
      const surveyQuestionPairs = questionData.surveyDetails.map(detail => ({
        surveyId: detail.surveyId,
        questionId: detail.questionId
      }));
      const selectedCampaignIds = Array.from(
        new Set(
          questionData.surveyDetails
            .map((detail) => detail.campaignId)
            .filter(Boolean)
        )
      );

      if (abortControllerRef.current) abortControllerRef.current.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Call endpoint (streaming NDJSON)
      const response = await fetchWithAuth(`${API_BASE_URL}/api/ia/generate-longtext-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/x-ndjson'
        },
        signal: controller.signal,
        returnRawResponse: true,
        body: JSON.stringify({
          clientId,
          surveyQuestionPairs,
          selectedCampaignIds,
          selectedQuestionCount: 1,
          startDate: startDate.toISOString().split('T')[0],
          endDate: endDate.toISOString().split('T')[0],
          force: false,
          stream: true
        })
      });

      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      const isNdjson = contentType.includes('application/x-ndjson');

      // Fallback (non-stream)
      if (!isNdjson || !response.body) {
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || `Erreur ${response.status}: ${response.statusText}`);
        }

        const result = await response.json();
        setAnalysis(result.result);
        setAnalysisMetadata({
          ownerName: result.owner_name,
          answersCount: result.answers_count,
          timestamp: result.timestamp,
          contextReduction: result.context_reduction || null
        });
        return;
      }

      // Stream NDJSON
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      let lastUiFlush = 0;

      const flushUi = () => {
        setAnalysis(accumulated);
        lastUiFlush = Date.now();
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) continue;

          let evt = null;
          try {
            evt = JSON.parse(trimmed);
          } catch (e) {
            continue;
          }

          if (evt.type === 'meta') {
            setAnalysisMetadata({
              ownerName: evt.owner_name,
              answersCount: evt.answers_count,
              timestamp: evt.timestamp,
              contextReduction: evt.context_reduction || null
            });
            continue;
          }

          if (evt.type === 'delta') {
            accumulated += evt.delta || '';
            const now = Date.now();
            if (now - lastUiFlush > 120) flushUi();
            continue;
          }

          if (evt.type === 'error') {
            setAnalysisError(evt.error || "Erreur lors de l'analyse");
            continue;
          }

          if (evt.type === 'done') {
            if (typeof evt.result === 'string') accumulated = evt.result;
            flushUi();
            if (evt.timestamp || evt.owner_name || evt.answers_count !== undefined) {
              setAnalysisMetadata({
                ownerName: evt.owner_name,
                answersCount: evt.answers_count,
                timestamp: evt.timestamp,
                contextReduction: evt.context_reduction || null
              });
            }
          }
        }
      }

      // Final flush (in case last chunk didn't pass throttle)
      setAnalysis(accumulated);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      console.error('Erreur lors de l\'analyse IA:', error);
      setAnalysisError(error.message || "Erreur lors de l'analyse");
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Fonction pour exporter en texte
  const exportAsText = () => {
    if (!analysis) return;

    const timestamp = new Date().toLocaleString('fr-FR');
    const content = `ANALYSE IA - RAPPORT CONSOLIDÉ
=====================================

Question analysée: ${questionData.questionText}
Nombre de campagnes: ${questionData.surveys.length}
Nombre de réponses: ${totalResponsesCount}
Date d'export: ${timestamp}
${analysisMetadata?.ownerName ? `Analysé par: ${analysisMetadata.ownerName}` : ''}

=====================================

${analysis}

=====================================
Rapport généré par Globe BI - Analyse IA
`;

    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analyse-ia-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Fonction pour exporter en PDF
  const exportAsPDF = async () => {
    if (!analysis) return;

    setIsExporting(true);
    try {
      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      const maxLineWidth = pageWidth - 2 * margin;

      let yPosition = margin;

      // Helper function to add text with automatic line wrapping
      const addWrappedText = (text, fontSize = 12, isBold = false) => {
        doc.setFontSize(fontSize);
        if (isBold) {
          doc.setFont(undefined, 'bold');
        } else {
          doc.setFont(undefined, 'normal');
        }

        const lines = doc.splitTextToSize(text, maxLineWidth);

        lines.forEach(line => {
          if (yPosition > doc.internal.pageSize.getHeight() - margin) {
            doc.addPage();
            yPosition = margin;
          }
          doc.text(line, margin, yPosition);
          yPosition += fontSize * 0.5;
        });

        yPosition += 5; // Add some spacing after each section
      };

      // Title
      addWrappedText('ANALYSE IA - RAPPORT CONSOLIDÉ', 16, true);
      yPosition += 10;

      // Metadata
      addWrappedText(`Question analysée: ${questionData.questionText}`, 10);
      addWrappedText(`Nombre de campagnes: ${questionData.surveys.length}`, 10);
      addWrappedText(`Nombre de réponses: ${totalResponsesCount}`, 10);
      addWrappedText(`Date d'export: ${new Date().toLocaleString('fr-FR')}`, 10);
      if (analysisMetadata?.ownerName) {
        addWrappedText(`Analysé par: ${analysisMetadata.ownerName}`, 10);
      }
      yPosition += 10;

      // Analysis content (parse markdown and convert to plain text)
      const plainTextAnalysis = analysis
        .replace(/#{1,6}\s*(.+)/g, '$1') // Remove markdown headers
        .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold markdown
        .replace(/\*(.+?)\*/g, '$1') // Remove italic markdown
        .replace(/- (.+)/g, '• $1') // Convert list items
        .replace(/^\s*$/gm, '') // Remove empty lines
        .replace(/\n{3,}/g, '\n\n'); // Limit to double line breaks

      addWrappedText(plainTextAnalysis, 11);

      // Footer
      if (yPosition > doc.internal.pageSize.getHeight() - 40) {
        doc.addPage();
        yPosition = margin;
      }

      doc.setFontSize(8);
      doc.setFont(undefined, 'normal');
      doc.text('Rapport généré par Globe BI - Analyse IA', margin, doc.internal.pageSize.getHeight() - 15);

      // Save the PDF
      doc.save(`analyse-ia-${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (error) {
      console.error('Erreur lors de l\'export PDF:', error);
      alert('Erreur lors de l\'export PDF. Veuillez réessayer.');
    }
    setIsExporting(false);
  };

  // Fonction pour exporter en image
  const exportAsImage = async () => {
    if (!analysisContentRef.current) return;

    setIsExporting(true);
    try {
      // Dynamically import html2canvas
      const html2canvas = (await import('html2canvas')).default;

      const canvas = await html2canvas(analysisContentRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `analyse-ia-${new Date().toISOString().split('T')[0]}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (error) {
      console.error('Erreur lors de l\'export image:', error);
      alert('Erreur lors de l\'export image. Veuillez réessayer.');
    }
    setIsExporting(false);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay */}
      <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" onClick={onClose}></div>

      {/* Modal Container */}
      <div className="flex min-h-full items-center justify-center p-4 sm:p-6 lg:p-8">
        <div
          className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-6xl w-full h-[90vh] overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 px-6 py-4 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                  Analyse - Question réponses consolidées
                </h1>
                <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">Question:</span>
                    <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                      {questionData.questionText}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span className="font-medium">Campagnes:</span>
                    <span>{questionData.surveys.length}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-2-2V10a2 2 0 012-2h8z" />
                    </svg>
                    <span className="font-medium">Réponses:</span>
                    <span>{totalResponsesCount}</span>
                  </div>
                </div>
              </div>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 flex-1 min-h-0 overflow-hidden">
            {/* AI Analysis Section */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 h-full min-h-0 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between mb-6 flex-shrink-0">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Analyse
                </h2>

                {/* Export buttons */}
                {analysis && !isAnalyzing && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={exportAsText}
                      disabled={isExporting}
                      className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                      title="Exporter en texte"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      TXT
                    </button>

                    <button
                      onClick={exportAsPDF}
                      disabled={isExporting}
                      className="px-3 py-2 text-sm bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 transition-colors flex items-center gap-2 disabled:opacity-50"
                      title="Exporter en PDF"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      PDF
                    </button>

                    <button
                      onClick={exportAsImage}
                      disabled={isExporting}
                      className="px-3 py-2 text-sm bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 transition-colors flex items-center gap-2 disabled:opacity-50"
                      title="Exporter en image"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      PNG
                    </button>

                    {isExporting && (
                      <div className="flex items-center gap-2 px-3 py-2">
                        <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm text-gray-600 dark:text-gray-400">Export...</span>
                      </div>
                    )}
                  </div>
                )}

                {!analysis && (
                  <button
                    onClick={handleAnalyze}
                    disabled={isAnalyzing || !questionData?.surveyDetails || questionData.surveyDetails.length === 0}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${
                      isAnalyzing || !questionData?.surveyDetails || questionData.surveyDetails.length === 0
                        ? 'bg-gray-200 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    {isAnalyzing ? (
                      <>
                        <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Analyse en cours...
                      </>
                    ) : (
                      <>
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                        Lancer l'analyse IA
                      </>
                    )}
                  </button>
                )}
              </div>

              <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                {/* Error Display */}
                {analysisError && (
                  <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4 mb-4 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-orange-700 dark:text-orange-300 font-medium">Oops!</span>
                    </div>
                    <p className="text-orange-600 dark:text-orange-400 mt-1">Une erreur d'analyse est survenue. Nos neurones artificielles sont en train de se remettre de cette erreur. Veuillez réessayer dans quelques instants, merci !</p>
                  </div>
                )}

                <div className="flex-1 min-h-0 overflow-hidden">
                  {/* Loading State (only until first streamed tokens arrive) */}
                  {isAnalyzing && (!analysis || analysis.length === 0) && (
                    <div className="h-full min-h-0 overflow-y-auto">
                      <FakeLoading
                        maxDuration={totalResponsesCount < 100 ? 8000 : totalResponsesCount < 500 ? 13000 : 20000}
                        className="py-8"
                      />
                    </div>
                  )}

                  {/* Analysis Result */}
                  {analysis && analysis.length > 0 ? (
                    <div className="h-full min-h-0 overflow-y-auto" ref={analysisContentRef}>
                      <div className="prose prose-lg dark:prose-invert max-w-none text-left pr-2">
                        <ReactMarkdown
                          components={{
                            h1: ({children}) => <h1 className="text-left text-2xl font-bold mb-4 text-gray-900 dark:text-white">{children}</h1>,
                            h2: ({children}) => <h2 className="text-left text-xl font-semibold mb-3 text-gray-800 dark:text-gray-100 mt-6">{children}</h2>,
                            h3: ({children}) => <h3 className="text-left text-lg font-medium mb-2 text-gray-700 dark:text-gray-200 mt-4">{children}</h3>,
                            p: ({children}) => <p className="text-left mb-3 text-gray-700 dark:text-gray-300 leading-relaxed">{children}</p>,
                            ul: ({children}) => <ul className="text-left list-disc pl-6 mb-4 space-y-1">{children}</ul>,
                            ol: ({children}) => <ol className="text-left list-decimal pl-6 mb-4 space-y-1">{children}</ol>,
                            li: ({children}) => <li className="text-left text-gray-700 dark:text-gray-300">{children}</li>,
                            strong: ({children}) => <strong className="text-left font-semibold text-gray-800 dark:text-gray-200">{children}</strong>,
                            em: ({children}) => <em className="text-left italic text-gray-600 dark:text-gray-400">{children}</em>,
                            hr: () => <hr className="my-6 border-gray-200 dark:border-gray-700" />,
                            blockquote: ({children}) => (
                              <blockquote className="text-left border-l-4 border-blue-500 pl-4 py-2 bg-blue-50 dark:bg-blue-900/20 italic text-blue-800 dark:text-blue-200 my-4">
                                {children}
                              </blockquote>
                            )
                          }}
                        >
                          {analysis}
                        </ReactMarkdown>
                      </div>
                    </div>
                  ) : !isAnalyzing ? (
                    <div className="h-full flex items-center justify-center">
                      <div className="text-center text-gray-500 dark:text-gray-400">
                        {!questionData?.surveyDetails || questionData.surveyDetails.length === 0 ? (
                          <div>
                            <svg className="mx-auto h-12 w-12 mb-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-lg font-medium mb-2">Aucune donnée disponible</p>
                            <p className="mb-4">Aucune donnée de survey n'est disponible pour cette question.</p>
                            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-left">
                              <div className="flex items-center gap-2 mb-2">
                                <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="font-medium text-blue-700 dark:text-blue-300">Informations disponibles :</span>
                              </div>
                              <ul className="text-blue-600 dark:text-blue-400 space-y-1">
                                <li>• <strong>{totalResponsesCount}</strong> réponses au total</li>
                                <li>• <strong>{questionData.surveys.length}</strong> formulaire{questionData.surveys.length > 1 ? 's' : ''} concerné{questionData.surveys.length > 1 ? 's' : ''}</li>
                                <li>• Question: <em>"{questionData.questionText}"</em></li>
                              </ul>
                            </div>
                          </div>
                        ) : (
                          <div>
                            <svg className="mx-auto h-12 w-12 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            <p className="text-lg font-medium mb-2">Prêt pour l'analyse</p>
                            <p>Cliquez sur "Lancer l'analyse IA" pour analyser les réponses de <strong>{totalResponsesCount}</strong> réponse{totalResponsesCount > 1 ? 's' : ''} consolidée{totalResponsesCount > 1 ? 's' : ''}.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GIAIAnalysisModal;